"use client";

import { LANGUAGE_SETTINGS_COPY } from "@tami/core";
import { LOCALE_NAMES, SUPPORTED_LOCALES, type Locale } from "@tami/core/intl";
import { Check, Globe } from "lucide-react";

import { cn } from "../../cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown-menu/dropdown-menu.web";
import type { LanguageMenuProps } from "./types";

/** "pt-BR" → "PT": a sigla do botão fechado. */
const shortCode = (locale: Locale): string => locale.slice(0, 2).toUpperCase();

/**
 * Seletor de idioma compacto das páginas públicas (cabeçalho da home): globo
 * com a sigla, abrindo os três idiomas pelo nome no próprio idioma. Na conta
 * o seletor é o `LanguagePreference`; os dois só recebem e devolvem o idioma.
 */
export function LanguageMenu({ value, onValueChange, className, contentClassName }: LanguageMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-menu"
        aria-label={`${LANGUAGE_SETTINGS_COPY.title}: ${LOCALE_NAMES[value]}`}
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-sm font-medium transition-opacity hover:opacity-70",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        <Globe className="size-4" aria-hidden />
        {shortCode(value)}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn("min-w-36", contentClassName)}>
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            data-testid={`language-option-${locale}`}
            lang={locale}
            onSelect={() => {
              if (locale !== value) onValueChange(locale);
            }}
            className="justify-between gap-3"
          >
            {LOCALE_NAMES[locale]}
            {locale === value ? <Check className="size-4" aria-hidden /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
